import React, { useEffect, useMemo, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import {
    Mail,
    MapPin,
    Package,
    Phone,
    Plus,
    User,
    X,
} from "lucide-react"
import Badge from "../common/Badge.jsx"
import Button from "../common/Button.jsx"
import OrderStatusBadge from "../orders/OrderStatusBadge.jsx"
import { formatContactMethodLabel } from "../utils/contactMethodLabel.js"

export default function CustomerDetailsSlideout({
    api,
    helpers,
    customer,
    isOpen,
    onClose,
    emptyMessage = "No customer selected.",
    onCreateOrderClick = () => { },
    onEditCustomerClick = () => { },
    onCommunicationClick = () => { },
    onSeeAllRecentOrdersClick = () => { },
    disableEditCustomer = false,
    editCustomerDisabledTooltip = "Cannot edit customers while offline.",
    enableStatus = true,
    enableCommunication = true,
    enableTypeBadges = true,
    enableLodge = true,
    ordersTableYearParam = undefined,
}) {
    const navigate = useNavigate()
    const formatStatus = helpers?.formatStatus ?? ((status) => status)
    const [recentOrdersLoading, setRecentOrdersLoading] = useState(false)
    const [recentOrdersError, setRecentOrdersError] = useState(
        /** @type {string | null} */(null)
    )
    const [recentOrdersTotal, setRecentOrdersTotal] = useState(0)
    const [recentOrders, setRecentOrders] = useState(/** @type {any[]} */([]))

    const customerId = customer?.id ?? null
    const isEmptyState = !customer

    useEffect(() => {
        if (!isOpen || !customerId) {
            return
        }
        let cancelled = false
        setRecentOrdersLoading(true)
        setRecentOrdersError(null)
        setRecentOrdersTotal(0)
        setRecentOrders([])

        api.getOrders(1, 10, "ordered_at", "desc", "", [`{customer_id = ${customerId}}`])
            .then((body) => {
                if (cancelled) {
                    return
                }
                setRecentOrders(Array.isArray(body?.data) ? body.data : [])
                setRecentOrdersTotal(Number(body?.total ?? 0) || 0)
            })
            .catch((error) => {
                if (cancelled) {
                    return
                }
                setRecentOrdersError(error?.message ?? "Failed to load recent orders.")
                setRecentOrders([])
                setRecentOrdersTotal(0)
            })
            .finally(() => {
                if (!cancelled) {
                    setRecentOrdersLoading(false)
                }
            })

        return () => {
            cancelled = true
        }
    }, [isOpen, customerId])

    const viewInTableLabel = useMemo(() => {
        if (recentOrdersTotal > 10) {
            return "View all in table"
        }
        return "View in table"
    }, [recentOrdersTotal])

    const seeAllOrdersHref = useMemo(() => {
        if (!customerId) {
            return "/v2/orders"
        }
        const base = `/v2/orders?customer_id=${encodeURIComponent(String(customerId))}`
        if (String(ordersTableYearParam ?? "").trim() === "") {
            return base
        }
        return `${base}&year=${encodeURIComponent(String(ordersTableYearParam))}`
    }, [customerId, ordersTableYearParam])

    const recentOrdersCountLabel = useMemo(() => {
        if (recentOrdersTotal > 10) {
            return "10+"
        }
        return String(recentOrders.length)
    }, [recentOrders.length, recentOrdersTotal])

    if (!isOpen && isEmptyState) {
        return null
    }

    if (isEmptyState) {
        return (
            <div
                className="position-fixed top-0 bottom-0 end-0 bg-white shadow-lg transition-transform"
                style={{
                    width: "480px",
                    maxWidth: "100%",
                    transform: isOpen ? "translateX(0)" : "translateX(100%)",
                    transitionDuration: "300ms",
                    transitionTimingFunction: "ease-in-out",
                    zIndex: 1040,
                }}
            >
                <div className="position-relative d-flex h-100">
                    <div className="w-100 d-flex flex-column">
                        <div
                            className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center text-white"
                            style={{ backgroundColor: "#0f6170" }}
                        >
                            <div>
                                <h2 className="fs-5 fw-semibold mb-1">Customer Details</h2>
                            </div>
                            <button
                                onClick={onClose}
                                className="btn btn-link text-white p-0 border-0"
                                style={{ opacity: 0.8 }}
                                type="button"
                            >
                                <X size={24} />
                            </button>
                        </div>
                        <div className="flex-grow-1 overflow-auto">
                            <div className="p-4">
                                <p className="mb-0 text-secondary">{emptyMessage}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div
            className="position-fixed top-0 bottom-0 end-0 bg-white shadow-lg transition-transform"
            style={{
                width: "480px",
                maxWidth: "100%",
                transform: isOpen ? "translateX(0)" : "translateX(100%)",
                transitionDuration: "300ms",
                transitionTimingFunction: "ease-in-out",
                zIndex: 1040,
            }}
        >
            <div className="position-relative d-flex h-100">
                <div className="w-100 d-flex flex-column">
                    <div
                        className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center text-white"
                        style={{ backgroundColor: "#0f6170" }}
                    >
                        <div>
                            <h2 className="fs-5 fw-semibold mb-1">
                                {customer.first_name ?? "—"} {customer.last_name ?? "—"}
                            </h2>
                            <p className="mb-0 small" style={{ opacity: 0.8 }}>
                                {customer.id}
                                {enableStatus ? (
                                    <>
                                        {" "}
                                        |{" "}
                                        <span
                                            className="text-capitalize v2-not-implemented-btn"
                                            title="Showing mock data"
                                        >
                                            {customer.status}
                                        </span>
                                    </>
                                ) : null}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="btn btn-link text-white p-0 border-0"
                            style={{ opacity: 0.8 }}
                            type="button"
                        >
                            <X size={24} />
                        </button>
                    </div>

                    <div className="flex-grow-1 overflow-auto">
                        <div className="p-4">
                            <div className="d-flex flex-wrap gap-2 mb-4">
                                <Button
                                    variant="primary"
                                    size="sm"
                                    icon={<span><Plus size={16} /></span>}
                                    onClick={() => onCreateOrderClick()}
                                >
                                    Create Order
                                </Button>
                                {disableEditCustomer ? (
                                    <span className="d-inline-block" title={editCustomerDisabledTooltip}>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled
                                        >
                                            Edit Customer
                                        </Button>
                                    </span>
                                ) : (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => onEditCustomerClick()}
                                    >
                                        Edit Customer
                                    </Button>
                                )}
                                {enableCommunication ? (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="v2-not-implemented-btn"
                                        onClick={() => onCommunicationClick()}
                                    >
                                        Communication
                                    </Button>
                                ) : null}
                            </div>

                            <div className="mb-4">
                                {enableTypeBadges ? (
                                    <div className="d-flex flex-wrap gap-1 mb-3">
                                        {(customer.customer_types ?? []).map((type) => (
                                            <Badge
                                                key={type}
                                                variant="info"
                                                className="small v2-not-implemented-btn"
                                            >
                                                {type}
                                            </Badge>
                                        ))}
                                    </div>
                                ) : null}
                                <h3 className="fs-5 fw-semibold text-dark mb-3 d-flex align-items-center">
                                    <User size={20} className="me-2" />
                                    Contact Information
                                </h3>
                                <div className="vstack gap-3">
                                    <div className="d-flex align-items-center">
                                        <Mail size={16} className="text-muted me-3" />
                                        <div>
                                            <p className="small fw-semibold mb-0">
                                                {customer.email ?? "—"}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <Phone size={16} className="text-muted me-3" />
                                        <div>
                                            <p className="small fw-semibold mb-0">
                                                {customer.phone ?? "—"}
                                            </p>
                                        </div>
                                    </div>
                                    {customer.company && (
                                        <div className="d-flex align-items-center">
                                            <Package size={16} className="text-muted me-3" />
                                            <div>
                                                <p className="small text-secondary mb-0">Company</p>
                                                <p className="small fw-semibold mb-0">
                                                    {customer.company}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    <div className="d-flex align-items-center">
                                        <Phone size={16} className="text-muted me-3" />
                                        <div>
                                            <p className="small text-secondary mb-0">
                                                Preferred Contact
                                            </p>
                                            <p className="small fw-semibold mb-0">
                                                {formatContactMethodLabel(customer.contact_method) || "—"}
                                            </p>
                                        </div>
                                    </div>
                                    {customer.is_staff && (
                                        <div className="d-flex align-items-center">
                                            <Badge variant="info">Staff Member</Badge>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="mb-4">
                                <h3 className="fs-5 fw-semibold text-dark mb-3 d-flex align-items-center">
                                    <Package size={20} className="me-2" />
                                    Current Lodge Info
                                </h3>
                                <div className="vstack gap-3">
                                    {enableLodge ? (
                                        <div className="d-flex align-items-center">
                                            <div
                                                className="flex-shrink-0 me-3 rounded-circle"
                                                style={{
                                                    width: "16px",
                                                    height: "16px",
                                                    backgroundColor: "#0f6170",
                                                }}
                                            />
                                            <div>
                                                <p
                                                    className="small text-secondary mb-0 v2-not-implemented-btn"
                                                    title="Showing mock data"
                                                >
                                                    Lodge
                                                </p>
                                                <p className="small fw-semibold mb-0">
                                                    {customer.lodge_info?.lodge || "—"}
                                                </p>
                                            </div>
                                        </div>
                                    ) : null}
                                    <div className="d-flex align-items-center">
                                        <div
                                            className="flex-shrink-0 me-3 rounded-circle"
                                            style={{
                                                width: "16px",
                                                height: "16px",
                                                backgroundColor: "#0f6170",
                                            }}
                                        />
                                        <div>
                                            <p className="small text-secondary mb-0">Trip Code</p>
                                            <p className="small fw-semibold mb-0">
                                                {customer.trip_code ?? "—"}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <div
                                            className="flex-shrink-0 me-3 rounded-circle"
                                            style={{
                                                width: "16px",
                                                height: "16px",
                                                backgroundColor: "#0f6170",
                                            }}
                                        />
                                        <div>
                                            <p className="small text-secondary mb-0">Fish Tag</p>
                                            <p className="small fw-semibold mb-0">
                                                {customer.fish_tag ?? "—"}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-4">
                                <h3 className="fs-5 fw-semibold text-dark mb-3 d-flex align-items-center">
                                    <MapPin size={20} className="me-2" />
                                    Shipping Address
                                </h3>
                                <div className="bg-light rounded p-3">
                                    {(String(customer.ship_to?.email ?? "").trim() !== "" || String(customer.email ?? "").trim() !== "") ? (
                                        <p className="small mb-1">
                                            <span className="text-secondary">Email:</span>{" "}
                                            {String(customer.ship_to?.email ?? "").trim() !== "" ? customer.ship_to.email : customer.email}
                                        </p>
                                    ) : null}
                                    {(String(customer.ship_to?.phone ?? "").trim() !== "" || String(customer.phone ?? "").trim() !== "") ? (
                                        <p className="small mb-1">
                                            <span className="text-secondary">Phone:</span>{" "}
                                            {String(customer.ship_to?.phone ?? "").trim() !== "" ? customer.ship_to.phone : customer.phone}
                                        </p>
                                    ) : null}
                                    <p className="small mb-1">
                                        {customer.ship_to?.street_address ?? "—"}
                                    </p>
                                    <p className="small mb-1">
                                        {customer.ship_to?.city ?? "—"},{" "}
                                        {customer.ship_to?.state ?? "—"}
                                    </p>
                                    <p className="small mb-0">
                                        {customer.ship_to?.country ?? "—"}{" "}
                                        {customer.ship_to?.zip ?? "—"}
                                    </p>
                                </div>
                            </div>

                            <div className="mb-4">
                                <h3 className="fs-5 fw-semibold text-dark mb-3">
                                    Billing Address
                                </h3>
                                <div className="bg-light rounded p-3">
                                    {customer.bill_same_as_ship ? (
                                        <p className="small text-secondary fst-italic mb-0">
                                            Same as shipping address
                                        </p>
                                    ) : (
                                        <>
                                            {(String(customer.bill_to?.email ?? "").trim() !== "" || String(customer.email ?? "").trim() !== "") ? (
                                                <p className="small mb-1">
                                                    <span className="text-secondary">Email:</span>{" "}
                                                    {String(customer.bill_to?.email ?? "").trim() !== "" ? customer.bill_to.email : customer.email}
                                                </p>
                                            ) : null}
                                            {(String(customer.bill_to?.phone ?? "").trim() !== "" || String(customer.phone ?? "").trim() !== "") ? (
                                                <p className="small mb-1">
                                                    <span className="text-secondary">Phone:</span>{" "}
                                                    {String(customer.bill_to?.phone ?? "").trim() !== "" ? customer.bill_to.phone : customer.phone}
                                                </p>
                                            ) : null}
                                            <p className="small mb-1">
                                                {customer.bill_to?.street_address ?? "—"}
                                            </p>
                                            <p className="small mb-1">
                                                {customer.bill_to?.city ?? "—"},{" "}
                                                {customer.bill_to?.state ?? "—"}
                                            </p>
                                            <p className="small mb-0">
                                                {customer.bill_to?.country ?? "—"}{" "}
                                                {customer.bill_to?.zip ?? "—"}
                                            </p>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="mb-4">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h3 className="fs-5 fw-semibold text-dark mb-0">
                                        Recent Orders ({recentOrdersCountLabel})
                                    </h3>
                                    <Link
                                        className="btn btn-link p-0 border-0"
                                        style={{ color: "#0f6170", fontSize: "0.875rem" }}
                                        to={seeAllOrdersHref}
                                        onClick={() => {
                                            onClose?.()
                                            onSeeAllRecentOrdersClick?.()
                                        }}
                                    >
                                        {viewInTableLabel}
                                    </Link>
                                </div>
                                <div className="vstack gap-3">
                                    {recentOrdersLoading ? (
                                        <div className="bg-light rounded p-3">
                                            <div className="d-flex align-items-center gap-2 text-secondary">
                                                <span
                                                    className="spinner-border spinner-border-sm"
                                                    role="status"
                                                    aria-hidden
                                                />
                                                <span>Loading recent orders…</span>
                                            </div>
                                        </div>
                                    ) : recentOrdersError ? (
                                        <div className="bg-light rounded p-3 text-danger small">
                                            {recentOrdersError}
                                        </div>
                                    ) : recentOrders.length > 0 ? (
                                        recentOrders.map((order) => (
                                            <button
                                                key={order.id}
                                                type="button"
                                                className="bg-light rounded p-3 border-0 text-start"
                                                style={{ cursor: "pointer" }}
                                                onClick={() => {
                                                    if (order?.id == null) {
                                                        return
                                                    }
                                                    onClose?.()
                                                    navigate(`/v2/orders/${order.id}/order`)
                                                }}
                                            >
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <div>
                                                        <p
                                                            className="fw-semibold mb-1"
                                                            style={{ color: "#0f6170" }}
                                                        >
                                                            #{order.master ?? order.id ?? "—"}
                                                        </p>
                                                        <p className="small text-secondary mb-0">
                                                            {order.ordered_at?.split?.("T")?.[0] ?? "—"}
                                                        </p>
                                                    </div>
                                                    <OrderStatusBadge
                                                        status={order.status}
                                                        label={formatStatus(order.status)}
                                                    />
                                                </div>
                                            </button>
                                        ))
                                    ) : (
                                        <p className="small text-muted fst-italic mb-0">
                                            No orders found
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
